import React from "react";

import { ChevronRight } from "lucide-react";
import { Accordion as AccordionPrimitive } from "radix-ui";
import { useTranslation } from "react-i18next";

import { AccordionContent, AccordionItem } from "@/src/components/ui/accordion";
import { Badge } from "@/src/components/ui/badge";
import { Button } from "@/src/components/ui/button";
import { maskAccessKey } from "@/src/utils/maskAccessKey";

import ProviderIcon from "./ProviderIcon";
import StatusPill from "./StatusPill";

import type { JSX } from "react";

import type { StorageAccess } from "@/src/api/ProjectAPI.types";

interface StorageAccessRowProps {
  access: StorageAccess;
  canWrite: boolean;
  isTesting: boolean;
  onEdit: (access: StorageAccess) => void;
  onDelete: (access: StorageAccess) => void;
  onTest: (access: StorageAccess) => void;
}

function formatVerifiedAt(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString();
}

function Detail({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className={mono ? "font-mono" : undefined}>{value}</span>
    </div>
  );
}

export default function StorageAccessRow({
  access,
  canWrite,
  isTesting,
  onEdit,
  onDelete,
  onTest,
}: StorageAccessRowProps): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const uri = access.host
    ? `${access.bucket_url} · ${access.host}`
    : access.bucket_url;

  return (
    <AccordionItem className="border-border px-6.25" value={access.uuid}>
      <AccordionPrimitive.Header className="flex items-center gap-3">
        <AccordionPrimitive.Trigger className="group flex flex-1 items-center gap-3 py-4 text-left outline-none">
          <ChevronRight className="text-muted-foreground size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          <ProviderIcon provider={access.type} />
          <span className="font-medium">{access.label}</span>
          <span className="text-muted-foreground font-mono text-sm">{uri}</span>
          {access.default ? (
            <Badge className="text-primary border-primary/30" variant="outline">
              {t("storageAccess.badgeDefault")}
            </Badge>
          ) : null}
        </AccordionPrimitive.Trigger>
        <StatusPill status={access.last_test_status} />
      </AccordionPrimitive.Header>
      <AccordionContent className="pb-4">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <Detail
            label={t("storageAccess.columns.region")}
            value={access.region}
          />
          <Detail
            mono
            label={t("storageAccess.columns.host")}
            value={access.host ?? "—"}
          />
          <Detail
            mono
            label={t("storageAccess.columns.accessKey")}
            value={maskAccessKey(access.access_key)}
          />
          <Detail
            label={t("storageAccess.columns.verifiedAt")}
            value={formatVerifiedAt(access.last_tested_at)}
          />
          <Detail
            label={t("storageAccess.columns.updatedAt")}
            value={formatDate(access.updated_at)}
          />
        </div>
        {canWrite ? (
          <div className="mt-4 flex gap-2">
            <Button
              disabled={isTesting}
              onClick={() => onTest(access)}
              size="sm"
              variant="outline"
            >
              {isTesting
                ? t("storageAccess.actions.testing")
                : t("storageAccess.actions.test")}
            </Button>
            <Button onClick={() => onEdit(access)} size="sm" variant="outline">
              {t("storageAccess.actions.edit")}
            </Button>
            <Button
              onClick={() => onDelete(access)}
              size="sm"
              variant="outline"
            >
              {t("storageAccess.actions.delete")}
            </Button>
          </div>
        ) : null}
      </AccordionContent>
    </AccordionItem>
  );
}
